import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import * as signalR from '@microsoft/signalr';
import { Check, EyeOff, MessageCircleQuestion, RefreshCw, X } from 'lucide-react';
import { RootState } from '../../configuration';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import {
  approveAiQuestion,
  getAiQuestionQueue,
  hideAiQuestion,
  playAiQuestion,
  rejectAiQuestion,
  rewriteAiQuestion,
  AiQuestionQueue,
  AiQuestionQueueItem,
} from '../../api/payment/ai-question.api';
import { questionStatusLabel } from '../../utils/ai-question';
import { createResponseGate } from '../../utils/response-gate';
import usePollWhile from '../../hooks/use-poll-while/use-poll-while';

/**
 * Bảng điều khiển "Hỏi đáp AI trên live" của streamer: duyệt / từ chối câu hỏi mới, đọc câu trả
 * lời AI viết rồi quyết định viết lại / bỏ / phát lên live, xem lịch sử gần đây. Tự làm mới khi
 * hub đẩy `ReceiveAiQuestionUpdate` (nối theo userId của chính mình) và tải lại dự phòng mỗi
 * 10 giây.
 */

const RELOAD_FALLBACK_MS = 10000;

const formatCa = (value: number) => `${Number(value || 0).toLocaleString('vi-VN')}`;

const formatTime = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
  });
};

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

// Khai ngoài component trang để không bị dựng lại (mất trạng thái) mỗi lần trang render.
const QuestionRow: React.FC<{
  item: AiQuestionQueueItem;
  showStatus?: boolean;
  children?: React.ReactNode;
}> = ({ item, showStatus, children }) => (
  <li className="flex flex-col gap-2 border-b ui-divider py-3 last:border-b-0">
    <div className="flex flex-wrap items-center gap-1 text-sm ui-text">
      <span className="font-semibold">{item.askerName}</span>
      {item.isAnonymous && <span className="ui-muted">(ẩn danh)</span>}
      <span className="ui-muted">· {formatCa(item.priceCa)} Ca</span>
      <span className="ui-muted">· {formatTime(item.createdAt)}</span>
    </div>
    <div className="text-base font-medium ui-heading">{item.question}</div>
    {item.status === 'Answering' ? (
      <div className="text-sm italic ui-muted">AI đang viết…</div>
    ) : (
      item.answer && (
        <div className="rounded-lg bg-black/5 p-3 text-sm ui-text dark:bg-white/5">{item.answer}</div>
      )
    )}
    {showStatus && (
      <div className="text-xs ui-muted">
        {questionStatusLabel(item.status)}
        {item.status === 'Played' && item.playedAt ? ` lúc ${formatTime(item.playedAt)}` : ''}
        {item.failReason ? `. ${item.failReason}` : ''}
      </div>
    )}
    {children && <div className="flex flex-wrap justify-end gap-2">{children}</div>}
  </li>
);

const Section: React.FC<{ title: string; count?: number; children: React.ReactNode }> = ({
  title,
  count,
  children,
}) => (
  <section className="ui-card rounded-xl p-4">
    <h2 className="mb-1 text-lg font-bold ui-heading">
      {title}
      {count !== undefined && <span className="ml-2 text-sm font-medium ui-muted">{count}</span>}
    </h2>
    {children}
  </section>
);

const Empty: React.FC<{ text: string }> = ({ text }) => <p className="py-3 text-sm ui-muted">{text}</p>;

const btnBase =
  'inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60';
const btnPrimary = `${btnBase} bg-blue-600 text-white hover:bg-blue-700`;
const btnOutline = `${btnBase} border border-[#C8CCD8] dark:border-slate-600 ui-text hover:bg-black/5 dark:hover:bg-white/5`;
const btnDanger = `${btnBase} border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/30`;

const AiQuestionControlPage: React.FC = () => {
  const profile = useSelector((state: RootState) => state.features.auth.profile);
  const userId: string | undefined = profile?.userId;

  const [queue, setQueue] = useState<AiQuestionQueue | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  // Chặn bấm hai lần ngay cả trước khi React kịp render trạng thái "đang xử lý".
  const inFlightRef = useRef<Set<string>>(new Set());
  const loadedOnceRef = useRef(false);
  // Tải lại do hub, do tải lại dự phòng và sau mỗi thao tác có thể chồng nhau: chỉ áp lần mới nhất.
  const gateRef = useRef(createResponseGate());

  const load = useCallback(async () => {
    const token = gateRef.current.issue();
    try {
      const q = await getAiQuestionQueue();
      if (!gateRef.current.acceptRead(token)) return;
      setQueue(q);
      setLoadError(false);
      loadedOnceRef.current = true;
    } catch {
      setLoadError(true);
      // Chỉ báo lần đầu, không báo lặp mỗi lần tải lại dự phòng.
      if (!loadedOnceRef.current) toast.error('Không tải được hàng chờ hỏi đáp AI.');
    }
  }, []);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!userId) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${userId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    connection.on('ReceiveAiQuestionUpdate', () => loadRef.current());
    connection.onreconnected(() => loadRef.current());
    connection.start().catch(() => {
      // Không nối được hub: vẫn còn tải lại dự phòng 10 giây.
    });
    return () => {
      connection.stop();
    };
  }, [userId]);

  usePollWhile(!!userId, () => loadRef.current(), RELOAD_FALLBACK_MS);

  const run = async <T,>(
    key: string,
    fn: () => Promise<T>,
    onOk: (result: T) => void,
    fallbackError: string
  ) => {
    if (inFlightRef.current.has(key)) return;
    inFlightRef.current.add(key);
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      onOk(await fn());
    } catch (err) {
      toast.error(errorMessage(err, fallbackError));
    } finally {
      inFlightRef.current.delete(key);
      setBusy((b) => {
        const next = { ...b };
        delete next[key];
        return next;
      });
      loadRef.current();
    }
  };

  const handleApprove = (item: AiQuestionQueueItem) =>
    run(
      `approve:${item.id}`,
      () => approveAiQuestion(item.id),
      (res) => {
        if (res.status === 'Failed') {
          toast.error(res.failReason || 'AI chưa viết được câu trả lời, người hỏi không bị trừ Ca.');
        } else {
          toast.success('Đã duyệt, câu trả lời đã sẵn sàng.');
        }
      },
      'Duyệt câu hỏi thất bại'
    );

  const handleReject = (item: AiQuestionQueueItem) => {
    if (!window.confirm('Từ chối câu hỏi này? Người hỏi không bị trừ Ca.')) return;
    run(
      `reject:${item.id}`,
      () => rejectAiQuestion(item.id),
      () => toast.success('Đã từ chối câu hỏi.'),
      'Từ chối câu hỏi thất bại'
    );
  };

  const handleRewrite = (item: AiQuestionQueueItem) =>
    run(
      `rewrite:${item.id}`,
      () => rewriteAiQuestion(item.id),
      (res) => {
        if (res.status === 'Failed') {
          toast.error(res.failReason || 'AI chưa viết lại được, người hỏi không bị trừ Ca.');
        } else {
          toast.success('Đã viết lại câu trả lời.');
        }
      },
      'Viết lại câu trả lời thất bại'
    );

  const handlePlay = (item: AiQuestionQueueItem) =>
    run(
      `play:${item.id}`,
      () => playAiQuestion(item.id),
      () => toast.success('Đã phát câu hỏi lên live.'),
      'Phát lên live thất bại'
    );

  const handleHide = () =>
    run(
      'hide',
      () => hideAiQuestion(),
      () => toast.success('Đã ẩn câu đang hiện trên live.'),
      'Ẩn câu hỏi thất bại'
    );

  const answering = queue?.answering ?? [];
  const pending = queue?.pending ?? [];
  const ready = queue?.ready ?? [];
  const done = queue?.done ?? [];
  const showing = queue?.showing ?? null;

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 px-4 py-6 md:px-6">
      <div className="flex items-center gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-[#EAEFFA] text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
          <MessageCircleQuestion size={22} />
        </span>
        <div>
          <h1 className="text-[22px] font-bold ui-heading md:text-[26px]">Bảng điều khiển hỏi đáp AI</h1>
          <p className="text-[14px] ui-muted">
            Duyệt câu hỏi người xem gửi, đọc câu trả lời AI viết rồi phát lên live.
          </p>
        </div>
      </div>

      {loadError && !queue && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
          <span className="text-sm text-red-600 dark:text-red-400">Không tải được hàng chờ hỏi đáp AI.</span>
          <button
            type="button"
            onClick={() => load()}
            className="shrink-0 rounded-lg border border-red-300 px-3 py-1 text-sm text-red-600 dark:border-red-700 dark:text-red-400"
          >
            Thử lại
          </button>
        </div>
      )}

      {!queue && !loadError && (
        <div className="ui-card flex items-center gap-3 rounded-xl p-4 text-sm ui-muted" role="status">
          <span className="size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Đang tải hàng chờ…
        </div>
      )}

      {queue && (
        <div className="ui-card flex items-center justify-between gap-3 rounded-xl p-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold ui-heading">Đang hiện trên live</div>
            {showing ? (
              <div className="mt-1 truncate text-sm ui-text">
                <span className="font-medium">{showing.askerName}</span>
                <span className="ui-muted"> — {showing.question}</span>
              </div>
            ) : (
              <div className="mt-1 text-sm ui-muted">Hiện không có câu hỏi nào đang hiện trên live.</div>
            )}
          </div>
          {showing && (
            <button type="button" className={btnOutline} disabled={!!busy.hide} onClick={handleHide}>
              <EyeOff size={14} />
              {busy.hide ? 'Đang ẩn…' : 'Ẩn'}
            </button>
          )}
        </div>
      )}

      {queue && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Section title="Chờ duyệt" count={pending.length + answering.length}>
            {pending.length === 0 && answering.length === 0 ? (
              <Empty text="Chưa có câu hỏi nào chờ duyệt." />
            ) : (
              <ul>
                {answering.map((item) => (
                  <QuestionRow key={item.id} item={item} />
                ))}
                {pending.map((item) => (
                  <QuestionRow key={item.id} item={item}>
                    <button
                      type="button"
                      className={btnDanger}
                      disabled={!!busy[`reject:${item.id}`] || !!busy[`approve:${item.id}`]}
                      onClick={() => handleReject(item)}
                    >
                      <X size={14} />
                      {busy[`reject:${item.id}`] ? 'Đang từ chối…' : 'Từ chối'}
                    </button>
                    <button
                      type="button"
                      className={btnPrimary}
                      disabled={!!busy[`approve:${item.id}`] || !!busy[`reject:${item.id}`]}
                      onClick={() => handleApprove(item)}
                    >
                      <Check size={14} />
                      {busy[`approve:${item.id}`] ? 'Đang duyệt…' : 'Duyệt'}
                    </button>
                  </QuestionRow>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Chờ phát" count={ready.length}>
            {ready.length === 0 ? (
              <Empty text="Chưa có câu nào chờ phát." />
            ) : (
              <ul>
                {ready.map((item) => {
                  const anyBusy =
                    !!busy[`rewrite:${item.id}`] || !!busy[`reject:${item.id}`] || !!busy[`play:${item.id}`];
                  return (
                    <QuestionRow key={item.id} item={item}>
                      {item.rewriteCount < 1 && (
                        <button
                          type="button"
                          className={btnOutline}
                          disabled={anyBusy}
                          onClick={() => handleRewrite(item)}
                        >
                          <RefreshCw size={14} />
                          {busy[`rewrite:${item.id}`] ? 'Đang viết lại…' : 'Viết lại'}
                        </button>
                      )}
                      <button
                        type="button"
                        className={btnDanger}
                        disabled={anyBusy}
                        onClick={() => handleReject(item)}
                      >
                        <X size={14} />
                        {busy[`reject:${item.id}`] ? 'Đang bỏ…' : 'Bỏ'}
                      </button>
                      <button
                        type="button"
                        className={btnPrimary}
                        disabled={anyBusy}
                        onClick={() => handlePlay(item)}
                      >
                        <Check size={14} />
                        {busy[`play:${item.id}`] ? 'Đang phát…' : 'Phát lên live'}
                      </button>
                    </QuestionRow>
                  );
                })}
              </ul>
            )}
          </Section>

          <Section title="Đã xong" count={done.length}>
            {done.length === 0 ? (
              <Empty text="Chưa có câu nào trong lịch sử." />
            ) : (
              <ul>
                {done.map((item) => (
                  <QuestionRow key={item.id} item={item} showStatus />
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}
    </div>
  );
};

export default AiQuestionControlPage;
